/**
 * Сопровождение — канбан-доска для проектов «В работе».
 *
 * 7 столбцов-этапов (внутренняя воронка ведения проекта после подписания
 * договора): Поставка материалов → Фундамент → Стены → Крыша → Остекление →
 * Мангальная зона → Сдача всех работ.
 *
 * Drag-and-drop: карточки можно перетаскивать между этапами мышью
 * (HTML5 native DnD API — без сторонних библиотек).
 */
import { useMemo, useRef, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import {
  fetchProductionList, updateProductionProject,
  type ProductionProject,
} from "@/api/dashboard";
import { fmt } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";


interface SupportStage {
  key: string;
  label: string;
  accent: string;
}

const STAGES: SupportStage[] = [
  { key: "materials",  label: "Поставка материалов", accent: "#9c7ce5" },
  { key: "foundation", label: "Фундамент",           accent: "#5a4a3a" },
  { key: "walls",      label: "Стены",               accent: "#c84545" },
  { key: "roof",       label: "Крыша",               accent: "#e99a12" },
  { key: "glazing",    label: "Остекление",          accent: "#3390ec" },
  { key: "bbq",        label: "Мангальная зона",     accent: "#a85f12" },
  { key: "handover",   label: "Сдача всех работ",    accent: "var(--green)" },
];

const STAGE_LABEL: Record<string, string> = Object.fromEntries(
  STAGES.map(s => [s.key, s.label]),
);

const DRAG_MIME = "application/x-kb-support-project";

export function SupportPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const q = useQuery({
    queryKey: ["production", "in_progress"],
    queryFn: () => fetchProductionList({ status: "in_progress" }),
    refetchInterval: 30000,
  });

  const allItems = q.data?.items ?? [];
  const items = useMemo(() => filterProjects(allItems, search), [allItems, search]);

  // Группировка по support_stage. Без stage → первая колонка "materials".
  const grouped = useMemo(() => {
    const g: Record<string, ProductionProject[]> = {};
    for (const s of STAGES) g[s.key] = [];
    for (const p of items) {
      const stage = p.support_stage && STAGE_LABEL[p.support_stage] ? p.support_stage : "materials";
      g[stage].push(p);
    }
    return g;
  }, [items]);

  // Колонка, над которой сейчас тащим — для highlight
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // Авто-скролл канбана при перетаскивании к краям. requestAnimationFrame-цикл
  // запускается во время drag когда курсор у левого/правого края контейнера.
  const kanbanRef = useRef<HTMLDivElement | null>(null);
  const autoScrollRef = useRef<{ rafId: number | null; dir: number; speed: number }>({ rafId: null, dir: 0, speed: 0 });
  function stopAutoScroll() {
    const s = autoScrollRef.current;
    if (s.rafId !== null) cancelAnimationFrame(s.rafId);
    s.rafId = null; s.dir = 0; s.speed = 0;
  }
  function tickAutoScroll() {
    const el = kanbanRef.current;
    const s = autoScrollRef.current;
    if (!el || s.dir === 0) { s.rafId = null; return; }
    el.scrollLeft += s.dir * s.speed;
    s.rafId = requestAnimationFrame(tickAutoScroll);
  }
  function updateAutoScroll(clientX: number) {
    const el = kanbanRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const EDGE = 100;             // ширина "горячей зоны" у края, px
    const MAX_SPEED = 24;         // макс скорость скролла, px/frame
    const fromLeft = clientX - rect.left;
    const fromRight = rect.right - clientX;
    let dir = 0, speed = 0;
    if (fromLeft < EDGE && fromLeft >= 0) {
      dir = -1;
      speed = ((EDGE - fromLeft) / EDGE) * MAX_SPEED;
    } else if (fromRight < EDGE && fromRight >= 0) {
      dir = 1;
      speed = ((EDGE - fromRight) / EDGE) * MAX_SPEED;
    }
    const s = autoScrollRef.current;
    s.dir = dir; s.speed = speed;
    if (dir !== 0 && s.rafId === null) {
      s.rafId = requestAnimationFrame(tickAutoScroll);
    }
    if (dir === 0) stopAutoScroll();
  }
  // Глобально слушаем dragend/drop чтобы остановить авто-скролл, даже если
  // drop случился вне колонки.
  useEffect(() => {
    const cleanup = () => stopAutoScroll();
    window.addEventListener("dragend", cleanup);
    window.addEventListener("drop", cleanup);
    return () => {
      window.removeEventListener("dragend", cleanup);
      window.removeEventListener("drop", cleanup);
      stopAutoScroll();
    };
  }, []);

  // Мутация для смены этапа (с optimistic update)
  const moveMut = useMutation({
    mutationFn: ({ id, stage }: { id: number; stage: string }) =>
      updateProductionProject(id, { support_stage: stage } as never),
    onMutate: async ({ id, stage }) => {
      await qc.cancelQueries({ queryKey: ["production", "in_progress"] });
      const prev = qc.getQueryData<{ items: ProductionProject[] }>(["production", "in_progress"]);
      if (prev) {
        qc.setQueryData(["production", "in_progress"], {
          ...prev,
          items: prev.items.map(p => p.id === id ? { ...p, support_stage: stage } : p),
        });
      }
      return { prev };
    },
    onError: (e: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["production", "in_progress"], ctx.prev);
      alert("Ошибка: " + e.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["production"] });
    },
  });

  function handleDrop(e: React.DragEvent<HTMLDivElement>, targetStage: string) {
    e.preventDefault();
    setDragOverKey(null);
    const raw = e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData("text/plain");
    const data = parseDragPayload(raw);
    if (!data) return;
    if (data.fromStage === targetStage) return;
    moveMut.mutate({ id: data.id, stage: targetStage });
  }

  return (
    <div
      className="page"
      style={{
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 80px)",   // вычитаем топбар, остаётся под канбан
        minHeight: 400,
        boxSizing: "border-box",
      }}
    >
      <div className="card" style={{
        padding: 14, marginBottom: "var(--gap)", flexShrink: 0,
        background: "#ffffff", border: "1px solid #e6e6e6", borderRadius: 14,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 12, color: "var(--mute)", letterSpacing: 0.3, textTransform: "uppercase" }}>
              Сопровождение
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, color: "#222" }}>
              Воронка ведения проектов в работе
            </div>
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>
              Здесь только проекты со статусом «В работе». Перетащите карточку мышью
              в нужный этап. Активных: <strong>{items.length}</strong>
              {search && allItems.length !== items.length && <> из <strong>{allItems.length}</strong></>}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {searchOpen || search ? (
              <>
                <input
                  type="search"
                  autoFocus
                  className="input input-sm"
                  placeholder="Поиск по клиенту / локации / тегам…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onBlur={() => { if (!search) setSearchOpen(false); }}
                  style={{ width: 280, maxWidth: "100%" }}
                />
                <button type="button" className="btn btn-sm btn-ghost"
                        onClick={() => { setSearch(""); setSearchOpen(false); }}
                        title="Закрыть поиск">×</button>
              </>
            ) : (
              <button type="button" className="btn btn-sm" onClick={() => setSearchOpen(true)} title="Поиск">
                <Icon name="search" size={12}/>
              </button>
            )}
          </div>
        </div>
      </div>

      {q.isLoading ? (
        <div className="muted" style={{ padding: 30 }}>Загрузка…</div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--mute)", padding: 40 }}>
          Нет активных проектов «В работе».
        </div>
      ) : (
        <div
          className="support-kanban"
          ref={kanbanRef}
          onDragOver={(e) => updateAutoScroll(e.clientX)}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${STAGES.length}, minmax(340px, 1fr))`,
            gap: 12,
            alignItems: "stretch",
            overflowX: "auto",
            overflowY: "hidden",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {STAGES.map((stage) => {
            const col = grouped[stage.key];
            const sum = col.reduce((s, p) => s + (p.sum_contract || 0), 0);
            const isDragOver = dragOverKey === stage.key;
            return (
              <div
                key={stage.key}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (dragOverKey !== stage.key) setDragOverKey(stage.key);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverKey(prev => prev === stage.key ? null : prev);
                  }
                }}
                onDrop={(e) => handleDrop(e, stage.key)}
                style={{
                  // TG-style колонка: чистый светло-серый фон, мягкая граница, верхний акцент-цвет
                  background: isDragOver ? "#e8f5e9" : "#f4f4f5",
                  border: isDragOver ? `2px dashed ${stage.accent}` : "1px solid #e6e6e6",
                  borderTop: `3px solid ${stage.accent}`,
                  borderRadius: 14,
                  padding: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  overflow: "hidden",
                  height: "100%",
                  transition: "background 120ms",
                }}
              >
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "4px 8px 10px", borderBottom: "1px dotted var(--line)",
                  flexShrink: 0,
                }}>
                  <div>
                    <div style={{ fontSize: 11.5, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>
                      {stage.label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, marginTop: 2, color: "#222" }}>
                      {col.length}
                    </div>
                  </div>
                  {sum > 0 && (
                    <div style={{ fontSize: 11, color: "var(--mute)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {fmt.rub(sum)}
                    </div>
                  )}
                </div>
                <div style={{
                  display: "flex", flexDirection: "column", gap: 8,
                  overflowY: "auto", flex: 1, paddingRight: 4, paddingTop: 6, paddingLeft: 2,
                }}>
                  {col.length === 0 ? (
                    <div style={{ color: "var(--mute)", fontSize: 12, textAlign: "center", padding: 24 }}>
                      {isDragOver ? "Отпустите здесь" : "пусто"}
                    </div>
                  ) : (
                    col.map(p => (
                      <SupportCard key={p.id} p={p} currentStage={stage.key}/>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/** Универсальный фильтр сделок (используется и в SupportPage, и в ProductionPage).
 *  Поиск case-insensitive по client_name / location / work_dates / brick_color /
 *  gazebo_size / period_label / comment. Пустая строка — без фильтра. */
export function filterProjects(items: ProductionProject[], query: string): ProductionProject[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(p => {
    const haystack = [
      p.client_name, p.location, p.work_dates,
      p.brick_color, p.gazebo_size, p.period_label, p.comment,
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(q);
  });
}


function parseDragPayload(raw: string): { id: number; fromStage: string } | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed.id === "number" && typeof parsed.fromStage === "string") return parsed;
  } catch { /* ignore */ }
  return null;
}


function SupportCard({
  p, currentStage,
}: {
  p: ProductionProject;
  currentStage: string;
}) {
  const navigate = useNavigate();
  const fact = p.sum_actual ?? 0;
  const expect = p.sum_expected ?? 0;
  const pct = expect > 0 ? Math.min(100, Math.round((fact / expect) * 100)) : 0;
  const [dragging, setDragging] = useState(false);

  function onClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest("select, button")) return;
    void navigate({ to: "/production/$projectId" as "/", params: { projectId: String(p.id) } as never });
  }

  function onDragStart(e: React.DragEvent<HTMLDivElement>) {
    const payload = JSON.stringify({ id: p.id, fromStage: currentStage });
    e.dataTransfer.setData(DRAG_MIME, payload);
    e.dataTransfer.setData("text/plain", payload);
    e.dataTransfer.effectAllowed = "move";
    setDragging(true);
  }

  const tags = [p.brick_color, p.gazebo_size].filter(Boolean) as string[];

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={() => setDragging(false)}
      onClick={onClick}
      style={{
        // TG-bubble стиль: белый фон, мягкая тень, скруглённые углы
        background: "#ffffff",
        border: "1px solid #e6e6e6",
        borderRadius: 12,
        padding: "12px 14px",
        cursor: dragging ? "grabbing" : "grab",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        opacity: dragging ? 0.5 : 1,
        transition: "border-color 120ms, transform 120ms, opacity 120ms, box-shadow 120ms",
        flexShrink: 0,
        minHeight: 180,
        boxSizing: "border-box",
      }}
      onMouseEnter={(e) => {
        if (!dragging) {
          e.currentTarget.style.borderColor = "#4fae4e";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e6e6e6";
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "none";
      }}
    >
      <div style={{
        fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        overflow: "hidden", minHeight: 35,
      }}>
        {p.client_name}
      </div>

      <div style={{ fontSize: 12, color: "var(--mute)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minHeight: 15 }}>
        {p.location || " "}
      </div>

      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        fontSize: 13, paddingTop: 4, borderTop: "1px dotted var(--line)",
      }}>
        <div>
          <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Договор</div>
          <div style={{ fontWeight: 700, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" }}>
            {fmt.rub(p.sum_contract ?? 0)}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Получено</div>
          <div style={{
            fontWeight: 700, fontVariantNumeric: "tabular-nums",
            color: pct === 100 ? "var(--green)" : pct >= 50 ? "#a85f12" : "var(--brick)",
          }}>
            {expect > 0 ? `${pct}%` : "—"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, minHeight: 18 }}>
        {tags.map((t, i) => (
          <span key={i} style={{
            fontSize: 10.5, padding: "2px 8px", borderRadius: 10,
            background: "var(--paper-2)", color: "var(--ink-3)",
            border: "1px solid var(--line)",
          }}>{t}</span>
        ))}
      </div>

      <div style={{ fontSize: 11, color: "var(--mute)", minHeight: 14 }}>
        {p.work_dates || " "}
      </div>
    </div>
  );
}
